/**
 * Launcher curation — the platform admin's screen (plan Phase 5).
 *
 * Lives on the Usage & Billing shelf beside Tenant Budgets / Feature Pricing /
 * Catalog. That placement is a governance decision, taken explicitly: the shelf
 * IS the platform-admin boundary, and everything on it is cross-tenant. The
 * curation is the `customers` area (#337): the routes take
 * `requirePlatformCapability('customers', …)`, and a seat holding only
 * `customers:read` (viewer/guest) gets this screen read-only (`canEdit`) — it
 * may look at a client's launcher, not retarget it.
 *
 * ── Two things about the editing model ───────────────────────────────
 *
 * **The whole set is replaced on save, and the draft is explicit.** The server
 * takes `PUT …/landing-shortcuts` with the full ordered array and derives
 * `position` from the index, so reordering is atomic and needs no diffing.
 * The screen therefore holds a draft and saves on demand rather than writing per
 * keystroke: a per-row autosave over a whole-set replace would make every
 * intermediate state — including "I removed this to re-add it above" — a real,
 * live launcher for every user of that tenant.
 *
 * **This screen does not grant anything.** A row says an app APPEARS; each
 * user's read still intersects it with their own permission, and launching is
 * gated independently server-side. So an app pinned here is not thereby shared
 * with anyone, and the tile simply will not render for someone who cannot read
 * it. Worth remembering when reading the empty picker for a tenant whose apps
 * all live in one restricted folder.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  GripVertical, ChevronUp, ChevronDown, X, Search, TriangleAlert, ArrowLeft, Plus, ImageOff,
} from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { useToast, useDraftState, MiniappIcon, CARD, SECTION_LABEL } from '@papan/core-ui/ui'
import { hintAttrs } from '@papan/core-ui/ui/tooltipHint'
import { parseMiniappIcon, type MiniappIconSpec } from '@papan/types'
import {
  launcherAdminApi,
  type AdminLauncherApp,
  type AdminLauncherShortcut,
  type AdminTenantOption,
} from '../client/admin.js'

const TENANT_PAGE_SIZE = 25
const ROW_ICON_PX = 32

/**
 * Same three-way filter as Tenant Budgets, defaulting to organizations for the
 * same reason: every user has a personal tenant, so the unfiltered list is
 * mostly personal noise.
 *
 * `personal` is reachable rather than excluded. A launcher on a personal drive
 * is unusual but not meaningless — a single-seat whitelabel deployment is one —
 * and hardcoding the org filter would make those tenants uncurateable with no
 * way to find that out from the UI.
 */
type TenantFilter = 'organization' | 'personal' | 'all'
const TENANT_FILTERS: readonly TenantFilter[] = ['organization', 'personal', 'all']

/** A row in the draft, carrying enough app detail to render without a re-fetch. */
interface DraftRow {
  fileId: string
  label: string | null
  name: string
  rawName: string
  mounted: boolean
  icon: string | null
  /** Target stopped presenting as an app. Rendered, not filtered — this screen
   *  is the only place it can be found and removed. */
  missing: boolean
}

/**
 * Does this spec need a server-composited PNG?
 *
 * It matters here because that PNG is unreachable from this screen for EVERY
 * app, mounted or not: `GET /api/miniapp/:id/icon.png` is scoped to
 * `auth.tenantId`, and a platform admin is acting in the platform org, not in
 * the tenant being curated. So the picker passes no `refId` at all and flags
 * these rows instead of rendering a placeholder that silently misrepresents the
 * icon the client will actually see.
 */
function usesAssets(spec: MiniappIconSpec | null): boolean {
  return !!spec && (spec.bg?.type === 'image' || spec.image === true)
}

function toDraft(s: AdminLauncherShortcut): DraftRow {
  return {
    fileId: s.fileId,
    label: s.label,
    name: s.name ?? s.fileId,
    rawName: s.rawName ?? '',
    mounted: s.mounted ?? false,
    icon: s.icon ?? null,
    missing: s.missing,
  }
}

function appToDraft(a: AdminLauncherApp): DraftRow {
  return { fileId: a.fileId, label: null, name: a.name, rawName: a.rawName, mounted: a.mounted, icon: a.icon, missing: false }
}

/** Comparable form of a draft — what actually gets sent. */
const wire = (rows: DraftRow[]) => rows.map(r => ({ fileId: r.fileId, label: r.label?.trim() || null }))
/** The rows as the user typed them, an emptied label equal to none. */
const asTyped = (rows: DraftRow[]) => rows.map(r => ({ fileId: r.fileId, label: r.label || null }))

function AppTile({ row }: { row: { name: string; icon: string | null } }) {
  const spec = parseMiniappIcon(row.icon)
  return (
    <span className="relative shrink-0">
      {/* No `refId`: see `usesAssets` — the composited PNG is out of reach from
          the platform org, so asking for it would only 403 once per row. */}
      <MiniappIcon spec={spec} name={row.name} size={ROW_ICON_PX} refId={null} />
      {usesAssets(spec) && (
        <ImageOff
          size={11}
          className="absolute -bottom-0.5 -right-0.5 themed-text-muted"
          aria-hidden
        />
      )}
    </span>
  )
}

export function LauncherAdmin({
  isPlatformAdmin,
  /**
   * The deployment-wide "which surface is home" control, rendered above the
   * tenant picker.
   *
   * Passed in as a SLOT rather than imported, for the same reason
   * `CatalogAdmin` takes its metered section that way: the control lives in
   * `@papan/orgs` (with `budgetApi` and the console's layout primitives) and
   * this package does not depend on it. Injecting keeps the dependency arrow
   * where it is instead of adding a whole feature package for one section.
   *
   * ⚠ It is gated `settings:write`, NOT by this page's grant — see the
   * component. The caller threads that; do not derive it from
   * `isPlatformAdmin` here.
   */
  canEdit,
  landingSection,
  forTenant,
}: {
  isPlatformAdmin: boolean
  /**
   * `customers:write` (#337) — the server refuses the PUT without it. Without
   * it the screen is read-only: no picker of candidates, no reorder / relabel /
   * remove, no save bar. Required, so a new host can't forget it and render a
   * Save that only ever answers 403.
   */
  canEdit: boolean
  landingSection?: ReactNode
  /**
   * Curate ONE customer, skipping the picker entirely (D6).
   *
   * The Launcher destination keeps its picker — an operator who arrives there is
   * asking "which customer shall I curate?". But an operator already ON a
   * customer's page has answered that, and making them pick the same customer
   * again in another screen is the "three screens and two tenant pickers"
   * antipattern this redesign exists to remove.
   *
   * Passing this also hides the heading: embedded, the section already has one
   * from its host, and two would be the duplicate-heading problem the price book
   * hit when its two screens were merged.
   */
  forTenant?: { id: string; name?: string; type?: string } | null
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()

  // ── Tenant selection ───────────────────────────────────────────────
  // `name`/`type` are display-only and the embedded view shows neither, so the
  // host is not made to fetch them just to satisfy a shape.
  const [embeddedTenant] = useState<AdminTenantOption | null>(
    forTenant ? { id: forTenant.id, name: forTenant.name ?? '', type: forTenant.type ?? 'organization' } as AdminTenantOption : null,
  )
  // The tenant picked on the standalone screen is kept across a phone/desktop
  // layout swap (#897), so the swap lands back on the tenant whose draft below
  // it carries — not on the picker with the draft out of reach.
  const [pickedTenant, setTenant, { clear: clearPickedTenant }] =
    useDraftState<AdminTenantOption | null>(forTenant ? null : 'launcher:tenant', null)
  const tenant = embeddedTenant ?? pickedTenant
  const [tenantInput, setTenantInput] = useState('')
  const [tenantQuery, setTenantQuery] = useState('')
  const [tenants, setTenants] = useState<AdminTenantOption[] | null>(null)
  const [tenantTotal, setTenantTotal] = useState(0)
  const [listError, setListError] = useState<string | null>(null)
  const [tenantFilter, setTenantFilter] = useState<TenantFilter>('organization')

  useEffect(() => {
    const id = setTimeout(() => setTenantQuery(tenantInput.trim()), 300)
    return () => clearTimeout(id)
  }, [tenantInput])

  useEffect(() => {
    if (!isPlatformAdmin || tenant) return
    let cancelled = false
    launcherAdminApi.listTenants({
      limit: TENANT_PAGE_SIZE,
      type: tenantFilter === 'all' ? undefined : tenantFilter,
      search: tenantQuery || undefined,
    })
      .then(r => { if (!cancelled) { setTenants(r.tenants); setTenantTotal(r.total); setListError(null) } })
      .catch((e: unknown) => { if (!cancelled) setListError(e instanceof Error ? e.message : String(e)) })
    return () => { cancelled = true }
  }, [isPlatformAdmin, tenant, tenantQuery, tenantFilter])

  // ── The tenant's catalog + its current launcher ────────────────────
  const [apps, setApps] = useState<AdminLauncherApp[] | null>(null)
  const [saved, setSaved] = useState<DraftRow[]>([])
  // A draft over `saved` (#897, kept across a phone/desktop layout swap):
  // until edited it IS the server's set, so `load` shows through; once edited,
  // no `load` overwrites it — the new screen's first load after a swap must not
  // wipe the draft the swap carried. Save (after its re-read) and Discard drop it.
  const [draft, setDraft, { clear: clearDraft }] =
    useDraftState<DraftRow[]>(tenant ? `launcher:${tenant.id}` : null, saved, {
      // A label typed and deleted is no label again: such a list is no draft
      // (#897). NOT `wire()` — it trims, and the box writes every keystroke,
      // so "Sales " over a stored "Sales" would count as unchanged and eat
      // the space being typed.
      isSame: (a, b) => JSON.stringify(asTyped(a)) === JSON.stringify(asTyped(b)),
    })
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [appQuery, setAppQuery] = useState('')

  const load = useCallback(async (tenantId: string) => {
    setLoading(true)
    setLoadError(null)
    try {
      // Parallel: neither call depends on the other, and the picker is unusable
      // until both land anyway.
      const [a, s] = await Promise.all([
        launcherAdminApi.listApps(tenantId),
        launcherAdminApi.getShortcuts(tenantId),
      ])
      const rows = s.shortcuts.map(toDraft)
      setApps(a.apps)
      setSaved(rows)
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { if (tenant) void load(tenant.id) }, [tenant, load])

  const dirty = useMemo(
    () => JSON.stringify(wire(draft)) !== JSON.stringify(wire(saved)),
    [draft, saved],
  )

  const pinned = useMemo(() => new Set(draft.map(r => r.fileId)), [draft])
  const candidates = useMemo(() => {
    const q = appQuery.trim().toLowerCase()
    return (apps ?? [])
      .filter(a => !pinned.has(a.fileId))
      .filter(a => !q || a.name.toLowerCase().includes(q) || a.rawName.toLowerCase().includes(q))
  }, [apps, pinned, appQuery])

  // ── Draft edits ────────────────────────────────────────────────────
  const move = (from: number, to: number) => {
    if (to < 0 || to >= draft.length || from === to) return
    setDraft(prev => {
      const next = [...prev]
      const [row] = next.splice(from, 1)
      next.splice(to, 0, row!)
      return next
    })
  }
  const remove = (fileId: string) => setDraft(prev => prev.filter(r => r.fileId !== fileId))
  const add = (a: AdminLauncherApp) => setDraft(prev => [...prev, appToDraft(a)])
  const relabel = (fileId: string, label: string) =>
    setDraft(prev => prev.map(r => (r.fileId === fileId ? { ...r, label } : r)))

  // Plain HTML5 drag-reorder, same shape as the workspace TabBar's. No library:
  // one list, one axis, and the up/down buttons below are the accessible path —
  // drag alone would make ordering keyboard-unreachable.
  const dragFrom = useRef<number | null>(null)
  const [dragOver, setDragOver] = useState<number | null>(null)

  const save = async () => {
    if (!tenant) return
    setSaving(true)
    try {
      await launcherAdminApi.putShortcuts(tenant.id, wire(draft))
      // Re-read rather than trusting the draft: the server derives `position`
      // and is the authority on what was actually stored, so the post-save
      // baseline should be its answer, not ours.
      await load(tenant.id)
      clearDraft()
      toast.success(t('launcher.admin.saved'))
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : t('launcher.admin.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  if (!isPlatformAdmin) {
    return <div className="p-4 themed-text-muted text-sm">{t('settings.budget.noPlatformAdmin')}</div>
  }

  // ── Tenant picker ──────────────────────────────────────────────────
  if (!tenant) {
    return (
      <div className="max-w-5xl">
        <h2 className="themed-text text-xl font-semibold tracking-tight mb-6">
          {t('launcher.admin.title')}
        </h2>

        {/* Deployment-wide, so it belongs on THIS view and not on the
            per-tenant one below. The detail screen is scoped to one selected
            customer; a global switch rendered there would read as "this
            customer's landing" — and a real per-tenant override is a live
            proposal (plan Phase 8), so the two would be indistinguishable.

            It renders FIRST because it is the question with the larger blast
            radius: the tiles below are one customer's, this is every user's
            home screen. */}
        {landingSection}

        {/* ── Per-tenant curation, as its own band ─────────────────────
            The eyebrow is what makes the page read as two decisions rather
            than one long form: everything above is deployment-wide, everything
            below is scoped to whichever customer you pick. */}
        <section>
          <h3 className={`${SECTION_LABEL} block`}>{t('launcher.admin.curationHeading')}</h3>
          <p className="themed-text-muted text-sm mt-1 mb-4">{t('launcher.admin.pickTenant')}</p>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="flex items-center gap-1">
              {TENANT_FILTERS.map(f => (
                <button
                  key={f}
                  onClick={() => setTenantFilter(f)}
                  className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                    tenantFilter === f
                      ? 'bg-[color:var(--bill-tint)] text-[color:var(--bill-primary)] ring-1 ring-[color:var(--bill-ring)]'
                      : 'themed-text-muted hover:themed-text ring-1 ring-[color:var(--border-primary)]'
                  }`}
                  data-testid="launcher-admin-tenant-filter"
                  data-filter={f}
                >
                  {t(`launcher.admin.tenantFilter.${f}`)}
                </button>
              ))}
            </div>

            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 themed-text-muted" />
              <input
                value={tenantInput}
                onChange={e => setTenantInput(e.target.value)}
                placeholder={t('launcher.admin.searchTenants')}
                className="themed-input w-full pl-9 pr-3 py-2 text-sm rounded-xl"
                data-testid="launcher-admin-tenant-search"
              />
            </div>
          </div>

          {listError && <div className="text-sm text-red-400 py-2">{listError}</div>}
          {!listError && tenants === null && (
            <div className="text-sm themed-text-muted py-2">{t('common:loading')}</div>
          )}
          {tenants?.length === 0 && (
            <div className="text-sm themed-text-muted py-2">{t('launcher.admin.noTenants')}</div>
          )}
          {!!tenants?.length && (
            <div className={`${CARD} overflow-hidden divide-y divide-[color:var(--border-primary)]`}>
              {tenants.map(row => (
                <button
                  key={row.id}
                  onClick={() => setTenant(row)}
                  className="group w-full flex items-center gap-3 px-4 py-3 text-left hover:themed-bg-tertiary transition-colors"
                  data-testid="launcher-admin-tenant-row"
                >
                  <span className="min-w-0">
                    <span className="themed-text text-sm truncate block">{row.name}</span>
                    {/* WHICH tenant: two personal tenants can share a name. */}
                    {(() => {
                      const who = tenantPickerEmail(row, t)
                      if (!who) return null
                      return (
                        <span className="themed-text-muted text-[11px] flex items-center gap-1 min-w-0">
                          <span className="truncate min-w-0" title={who.email}>{who.email}</span>
                          {who.mark && <span className="shrink-0 whitespace-nowrap themed-chip-warn">({who.mark})</span>}
                        </span>
                      )
                    })()}
                  </span>
                  <span className="themed-text-muted text-[10px] uppercase tracking-wide ml-auto shrink-0 px-2 py-0.5 rounded-full themed-bg-tertiary">
                    {row.type}
                  </span>
                  {/* Points at the drill-down, so a row reads as a destination
                      rather than a toggle. Rotated ChevronUp — this package's
                      icon set has no ChevronRight import to spare. */}
                  <ChevronUp
                    size={14}
                    className="rotate-90 shrink-0 themed-text-muted opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-hidden
                  />
                </button>
              ))}
            </div>
          )}
          {tenants && tenantTotal > tenants.length && (
            // No pager: the search box is the way to reach the rest. Said out
            // loud rather than silently truncating, which reads as "that's all".
            <p className="themed-text-muted text-xs mt-2">
              {t('launcher.admin.moreTenants', { shown: tenants.length, total: tenantTotal })}
            </p>
          )}
        </section>
      </div>
    )
  }

  // ── Curation for the selected tenant ───────────────────────────────
  return (
    <div className="max-w-5xl">
      {/* Embedded on a customer's own page the heading and the back arrow are
          both wrong: the host already says whose page this is, and "change
          customer" would send the operator out of the customer they are
          looking at. */}
      {!forTenant && (
        <>
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={() => { clearDraft(); clearPickedTenant(); setApps(null); setSaved([]); setAppQuery('') }}
              className="themed-text-muted hover:themed-text transition-colors"
              data-tooltip={t('launcher.admin.changeTenant')}
              aria-label={t('launcher.admin.changeTenant')}
            >
              <ArrowLeft size={16} />
            </button>
            <h2 className="themed-text text-lg font-medium">{t('launcher.admin.title')}</h2>
          </div>
          <p className="themed-text-muted text-sm mb-4">
            {t('launcher.admin.editingFor', { tenant: tenant.name })}
          </p>
        </>
      )}

      {loadError && <div className="text-sm text-red-400 mb-3">{loadError}</div>}
      {loading && <div className="text-sm themed-text-muted mb-3">{t('common:loading')}</div>}

      {!canEdit && (
        <p className="themed-text-muted text-sm mb-4" data-testid="launcher-admin-read-only">
          {t('launcher.admin.readOnly')}
        </p>
      )}

      {/* ── The launcher itself ── */}
      <h3 className="ws-home-heading mb-2">{t('launcher.admin.onLauncher')}</h3>
      {draft.length === 0 ? (
        <p className="themed-text-muted text-sm mb-5">{t('launcher.admin.noneChosen')}</p>
      ) : (
        <div className="ws-home-list mb-5" data-testid="launcher-admin-chosen">
          {draft.map((row, i) => (
            <div
              key={row.fileId}
              draggable={canEdit}
              onDragStart={() => { dragFrom.current = i }}
              onDragOver={e => { e.preventDefault(); if (dragFrom.current !== null && dragFrom.current !== i) setDragOver(i) }}
              onDragLeave={() => setDragOver(null)}
              onDrop={e => {
                e.preventDefault()
                if (dragFrom.current !== null) move(dragFrom.current, i)
                dragFrom.current = null
                setDragOver(null)
              }}
              onDragEnd={() => { dragFrom.current = null; setDragOver(null) }}
              className={`flex items-center gap-3 px-3 py-2 transition-colors ${dragOver === i ? 'themed-bg-tertiary' : ''}`}
              data-testid="launcher-admin-chosen-row"
              data-file-id={row.fileId}
            >
              {canEdit && <GripVertical size={14} className="themed-text-muted shrink-0 cursor-grab" aria-hidden />}
              {row.missing ? (
                <TriangleAlert size={ROW_ICON_PX} className="text-amber-500 shrink-0 p-1.5" aria-hidden />
              ) : (
                <AppTile row={row} />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className={`text-sm truncate ${row.missing ? 'themed-text-muted' : 'themed-text'}`}>
                    {row.missing ? row.fileId : row.name}
                  </span>
                  {row.mounted && (
                    <span className="text-[10px] uppercase tracking-wide themed-text-muted shrink-0">
                      {t('launcher.admin.mounted')}
                    </span>
                  )}
                </div>
                {row.missing && (
                  // The read path hides these; an admin would otherwise have no
                  // way to learn why a tile silently vanished for everyone.
                  <p className="text-xs text-amber-500">{t('launcher.admin.missingApp')}</p>
                )}
              </div>
              {canEdit ? (<>
              <input
                value={row.label ?? ''}
                onChange={e => relabel(row.fileId, e.target.value)}
                placeholder={row.missing ? '' : row.name}
                className="themed-input w-40 shrink-0 px-2 py-1 text-xs rounded"
                aria-label={t('launcher.admin.labelFor', { name: row.name })}
                data-testid="launcher-admin-label"
              />
              <button onClick={() => move(i, i - 1)} disabled={i === 0}
                className="themed-text-muted hover:themed-text disabled:opacity-30 shrink-0"
                {...hintAttrs(t('launcher.admin.moveUp'), i === 0)} aria-label={t('launcher.admin.moveUp')}>
                <ChevronUp size={14} />
              </button>
              <button onClick={() => move(i, i + 1)} disabled={i === draft.length - 1}
                className="themed-text-muted hover:themed-text disabled:opacity-30 shrink-0"
                {...hintAttrs(t('launcher.admin.moveDown'), i === draft.length - 1)} aria-label={t('launcher.admin.moveDown')}>
                <ChevronDown size={14} />
              </button>
              <button onClick={() => remove(row.fileId)}
                className="themed-text-muted hover:themed-text shrink-0"
                data-tooltip={t('launcher.admin.remove')} aria-label={t('launcher.admin.remove')}>
                <X size={14} />
              </button>
              </>) : (
                row.label && <span className="themed-text-muted text-xs shrink-0 truncate max-w-40">{row.label}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Candidates ── (only for a seat that may add them) */}
      {canEdit && (<>
      <h3 className="ws-home-heading mb-2">{t('launcher.admin.available')}</h3>
      <div className="relative mb-2">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 themed-text-muted" />
        <input
          value={appQuery}
          onChange={e => setAppQuery(e.target.value)}
          placeholder={t('launcher.admin.searchApps')}
          className="themed-input w-full pl-8 pr-3 py-1.5 text-sm rounded"
          data-testid="launcher-admin-app-search"
        />
      </div>
      {apps !== null && candidates.length === 0 ? (
        <p className="themed-text-muted text-sm">
          {apps.length === 0 ? t('launcher.admin.tenantHasNoApps') : t('launcher.admin.allPinned')}
        </p>
      ) : (
        <div className="ws-home-list" data-testid="launcher-admin-candidates">
          {candidates.map(a => (
            <button
              key={a.fileId}
              onClick={() => add(a)}
              className="w-full flex items-center gap-3 px-3 py-2 text-left hover:themed-bg-tertiary transition-colors"
              data-testid="launcher-admin-candidate"
              data-file-id={a.fileId}
            >
              <AppTile row={a} />
              <span className="themed-text text-sm truncate">{a.name}</span>
              {a.mounted && (
                <span className="text-[10px] uppercase tracking-wide themed-text-muted shrink-0">
                  {t('launcher.admin.mounted')}
                </span>
              )}
              <Plus size={14} className="themed-text-muted ml-auto shrink-0" aria-hidden />
            </button>
          ))}
        </div>
      )}

      </>)}

      {/* Icons that need a composited PNG can't be previewed from the platform
          org. Stated once, plainly, rather than per row — a broken image or a
          silent placeholder would both misrepresent what the client sees. */}
      <p className="themed-text-muted text-xs mt-3 flex items-center gap-1.5">
        <ImageOff size={11} aria-hidden />
        {t('launcher.admin.assetIconNote')}
      </p>

      {/* ── Save bar ── */}
      {canEdit && <div className="flex items-center gap-2 mt-6">
        <button
          onClick={() => void save()}
          disabled={!dirty || saving}
          className="themed-btn-primary px-3 py-1.5 text-sm rounded disabled:opacity-40"
          data-testid="launcher-admin-save"
        >
          {saving ? t('launcher.admin.saving') : t('launcher.admin.save')}
        </button>
        <button
          onClick={clearDraft}
          disabled={!dirty || saving}
          className="themed-btn-secondary px-3 py-1.5 text-sm rounded disabled:opacity-40"
          data-testid="launcher-admin-discard"
        >
          {t('launcher.admin.discard')}
        </button>
        {dirty && (
          <span className="themed-text-muted text-xs" data-testid="launcher-admin-dirty">
            {t('launcher.admin.unsaved')}
          </span>
        )}
      </div>}
    </div>
  )
}

/**
 * The email that says WHICH tenant a picker row is, and what it is: a personal
 * account's main email (marked when the account never proved it — a squat,
 * #595 — so it is not read as that person's), else an owner, else an owner
 * still invited. The mark is returned SEPARATELY so the caller renders it
 * outside the truncating email: a long address must not clip the warning away.
 */
function tenantPickerEmail(
  r: { primaryEmail?: string | null; primaryEmailVerified?: boolean | null; ownerEmails?: string[]; invitedOwnerEmails?: string[] },
  t: (key: string, opts: { defaultValue: string }) => string,
): { email: string; mark: string | null } | null {
  if (r.primaryEmail) {
    return {
      email: r.primaryEmail,
      mark: r.primaryEmailVerified === false
        ? t('settings.platform.customers.unverifiedEmail', { defaultValue: 'Unverified' })
        : null,
    }
  }
  if (r.ownerEmails?.[0]) return { email: r.ownerEmails[0], mark: null }
  if (r.invitedOwnerEmails?.[0]) {
    return { email: r.invitedOwnerEmails[0], mark: t('settings.platform.customers.invitedOwnerLabel', { defaultValue: 'Invited owner' }) }
  }
  return null
}
